import { z } from 'zod';
import {
  dimensionDefinitionSchema,
  observationKindSchema,
} from './dataset.ts';
import {
  jsonObjectSchema,
  jsonPrimitiveSchema,
  jsonValueSchema,
} from './json.ts';

export const modelExpressionSchema = z.string().min(1).max(500);
export const modelIdentifierSchema = z.string().regex(
  /^[a-z_]\w*$/i,
  '模型标识符必须是安全的字母、数字或下划线组合',
);

export const modelParameterSchema = z.object({
  id: modelIdentifierSchema,
  type: z.enum(['number', 'boolean', 'string']),
  default: jsonPrimitiveSchema,
  label: z.string().default(''),
  description: z.string().default(''),
  min: z.number().finite().optional(),
  max: z.number().finite().optional(),
}).strict().superRefine((parameter, context) => {
  if (
    parameter.min !== undefined
    && parameter.max !== undefined
    && parameter.min > parameter.max
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['max'],
      message: '参数上限不能小于下限',
    });
  }
});

export const modelStateVariableSchema = z.object({
  id: modelIdentifierSchema,
  type: z.enum(['number', 'boolean', 'string']),
  initial: jsonPrimitiveSchema,
}).strict();

export const modelEntityCollectionSchema = z.object({
  id: modelIdentifierSchema,
  idField: z.string().default('id'),
  initial: z.array(jsonObjectSchema).max(100000).default([]),
}).strict();

export const modelActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('state.set'),
    target: modelIdentifierSchema,
    value: modelExpressionSchema,
  }).strict(),
  z.object({
    type: z.literal('state.increment'),
    target: modelIdentifierSchema,
    value: modelExpressionSchema,
  }).strict(),
  z.object({
    type: z.literal('entity.create'),
    collection: modelIdentifierSchema,
    values: z.record(z.string(), modelExpressionSchema),
  }).strict(),
  z.object({
    type: z.literal('entity.update'),
    collection: modelIdentifierSchema,
    when: modelExpressionSchema.optional(),
    set: z.record(z.string(), modelExpressionSchema),
  }).strict(),
  z.object({
    type: z.literal('entity.delete'),
    collection: modelIdentifierSchema,
    when: modelExpressionSchema,
  }).strict(),
  z.object({
    type: z.literal('event.schedule'),
    eventType: z.string().min(1),
    delay: modelExpressionSchema,
    payload: z.record(z.string(), modelExpressionSchema).default({}),
  }).strict(),
]);

export const modelTimerSchema = z.object({
  id: modelIdentifierSchema,
  interval: modelExpressionSchema,
  initialDelay: modelExpressionSchema.optional(),
  when: modelExpressionSchema.optional(),
  repeat: z.boolean().default(true),
  actions: z.array(modelActionSchema).max(1000).default([]),
}).strict();

export const modelTransitionTriggerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('step') }).strict(),
  z.object({
    type: z.literal('timer'),
    timerId: modelIdentifierSchema,
  }).strict(),
  z.object({
    type: z.literal('event'),
    eventType: z.string().min(1),
  }).strict(),
  z.object({
    type: z.literal('interaction'),
    eventType: z.string().min(1),
  }).strict(),
]);

export const modelTransitionSchema = z.object({
  id: modelIdentifierSchema,
  trigger: modelTransitionTriggerSchema,
  when: modelExpressionSchema.optional(),
  actions: z.array(modelActionSchema).max(1000).default([]),
}).strict();

export const modelDerivedValueSchema = z.object({
  id: modelIdentifierSchema,
  expression: modelExpressionSchema,
}).strict();

export const modelObservationOutputSchema = z.object({
  id: z.string().min(1),
  kind: observationKindSchema,
  source: z.enum(['state', 'entities']),
  collection: modelIdentifierSchema.optional(),
  updateMode: z.enum(['replace', 'append']).default('replace'),
  maxRows: z.number().int().positive().max(100000).optional(),
  rowId: modelExpressionSchema.optional(),
  when: modelExpressionSchema.optional(),
  fields: z.record(z.string(), modelExpressionSchema),
  dimensions: z.array(dimensionDefinitionSchema).default([]),
  metadata: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    entityKey: z.string().optional(),
    timeKey: z.string().optional(),
  }).strict().default({}),
}).strict().superRefine((output, context) => {
  if (output.source === 'entities' && !output.collection) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['collection'],
      message: '实体数据输出必须指定集合',
    });
  }
  const fieldKeys = new Set(Object.keys(output.fields));
  const dimensionKeys = output.dimensions.map(item => item.key);
  const uniqueDimensionKeys = new Set(dimensionKeys);
  if (uniqueDimensionKeys.size !== dimensionKeys.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dimensions'],
      message: '输出维度 key 不能重复',
    });
  }
  const missing = [...fieldKeys].filter(key => !uniqueDimensionKeys.has(key));
  const extra = [...uniqueDimensionKeys].filter(key => !fieldKeys.has(key));
  if (missing.length > 0 || extra.length > 0) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dimensions'],
      message: `输出 fields 与 dimensions 必须一一对应（缺少：${missing.join('、') || '无'}；多余：${extra.join('、') || '无'}）`,
    });
  }
});

export const modelInteractionRuleSchema = z.object({
  eventType: z.string().min(1),
  when: modelExpressionSchema.optional(),
  actions: z.array(modelActionSchema).max(1000).default([]),
}).strict();

export const modelExecutionLimitsSchema = z.object({
  maxEntitiesPerCollection: z.number().int().positive().max(1000000).default(100000),
  maxScheduledEvents: z.number().int().positive().max(1000000).default(100000),
  maxActionsPerStep: z.number().int().positive().max(100000).default(10000),
  maxTimerExecutionsPerStep: z.number().int().positive().max(100000).default(1000),
  maxObservationRows: z.number().int().positive().max(100000).default(10000),
}).strict();

export const modelSpecSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  id: z.string().min(1),
  title: z.string().default(''),
  description: z.string().default(''),
  clock: z.object({
    mode: z.enum(['fixed-step', 'discrete-event']).default('fixed-step'),
    timeStep: z.number().positive().max(1000).default(0.1),
  }).strict().default({
    mode: 'fixed-step',
    timeStep: 0.1,
  }),
  parameters: z.array(modelParameterSchema).max(1000).default([]),
  state: z.array(modelStateVariableSchema).max(1000).default([]),
  entities: z.array(modelEntityCollectionSchema).max(1000).default([]),
  timers: z.array(modelTimerSchema).max(1000).default([]),
  transitions: z.array(modelTransitionSchema).max(5000).default([]),
  derived: z.array(modelDerivedValueSchema).max(1000).default([]),
  outputs: z.array(modelObservationOutputSchema).max(1000).default([]),
  interactions: z.array(modelInteractionRuleSchema).max(1000).default([]),
  limits: modelExecutionLimitsSchema.default({
    maxEntitiesPerCollection: 100000,
    maxScheduledEvents: 100000,
    maxActionsPerStep: 10000,
    maxTimerExecutionsPerStep: 1000,
    maxObservationRows: 10000,
  }),
  metadata: z.record(z.string(), jsonValueSchema).default({}),
}).strict().superRefine((model, context) => {
  const reservedIdentifiers = new Set([
    'abs',
    'binomial',
    'bernoulli',
    'ceil',
    'clamp',
    'cos',
    'count',
    'e',
    'entity',
    'event',
    'exp',
    'exponential',
    'false',
    'first',
    'floor',
    'index',
    'last',
    'log',
    'max',
    'min',
    'normal',
    'null',
    'parameters',
    'payload',
    'pi',
    'poisson',
    'pow',
    'random',
    'round',
    'sampleDistinctCount',
    'sampleAggregate',
    'sampleCoverageDrawCount',
    'sampleGeometricWaitCount',
    'sampleMean',
    'sin',
    'sqrt',
    'state',
    'tan',
    'tick',
    'time',
    'true',
    'uniform',
  ]);
  const unique = (
    values: string[],
    path: Array<string | number>,
    label: string,
  ) => {
    const seen = new Set<string>();
    for (const [index, value] of values.entries()) {
      if (seen.has(value)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...path, index, 'id'],
          message: `${label}编号重复：${value}`,
        });
      }
      seen.add(value);
    }
  };
  unique(model.parameters.map(item => item.id), ['parameters'], '参数');
  unique(model.state.map(item => item.id), ['state'], '状态变量');
  unique(model.entities.map(item => item.id), ['entities'], '实体集合');
  unique(model.timers.map(item => item.id), ['timers'], '定时器');
  unique(model.transitions.map(item => item.id), ['transitions'], '状态转移');
  unique(model.derived.map(item => item.id), ['derived'], '派生值');
  unique(model.outputs.map(item => item.id), ['outputs'], '观察输出');

  const namespaceEntries = [
    ...model.parameters.map((item, index) => ({
      id: item.id,
      path: ['parameters', index, 'id'] as Array<string | number>,
    })),
    ...model.state.map((item, index) => ({
      id: item.id,
      path: ['state', index, 'id'] as Array<string | number>,
    })),
    ...model.entities.map((item, index) => ({
      id: item.id,
      path: ['entities', index, 'id'] as Array<string | number>,
    })),
    ...model.derived.map((item, index) => ({
      id: item.id,
      path: ['derived', index, 'id'] as Array<string | number>,
    })),
  ];
  const namespace = new Set<string>();
  for (const entry of namespaceEntries) {
    if (reservedIdentifiers.has(entry.id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: entry.path,
        message: `模型标识符为保留名称：${entry.id}`,
      });
    }
    if (namespace.has(entry.id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: entry.path,
        message: `模型命名空间存在重复标识符：${entry.id}`,
      });
    }
    namespace.add(entry.id);
  }

  const stateIds = new Set(model.state.map(item => item.id));
  const stateById = new Map(model.state.map(item => [item.id, item]));
  const collectionIds = new Set(model.entities.map(item => item.id));
  const timerIds = new Set(model.timers.map(item => item.id));
  for (const [index, transition] of model.transitions.entries()) {
    if (
      transition.trigger.type === 'timer'
      && !timerIds.has(transition.trigger.timerId)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['transitions', index, 'trigger', 'timerId'],
        message: '状态转移引用了未知定时器',
      });
    }
  }

  const actions = [
    ...model.timers.flatMap(item => item.actions),
    ...model.transitions.flatMap(item => item.actions),
    ...model.interactions.flatMap(item => item.actions),
  ];
  for (const [index, parameter] of model.parameters.entries()) {
    const defaultMatches = (
      (parameter.type === 'number' && typeof parameter.default === 'number')
      || (parameter.type === 'boolean' && typeof parameter.default === 'boolean')
      || (parameter.type === 'string' && typeof parameter.default === 'string')
    );
    if (!defaultMatches) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['parameters', index, 'default'],
        message: `参数默认值类型应为 ${parameter.type}`,
      });
    }
    if (
      typeof parameter.default === 'number'
      && parameter.min !== undefined
      && parameter.default < parameter.min
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['parameters', index, 'default'],
        message: '参数默认值小于下限',
      });
    }
    if (
      typeof parameter.default === 'number'
      && parameter.max !== undefined
      && parameter.default > parameter.max
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['parameters', index, 'default'],
        message: '参数默认值大于上限',
      });
    }
  }
  for (const [index, variable] of model.state.entries()) {
    const initialMatches = (
      (variable.type === 'number' && typeof variable.initial === 'number')
      || (variable.type === 'boolean' && typeof variable.initial === 'boolean')
      || (variable.type === 'string' && typeof variable.initial === 'string')
    );
    if (!initialMatches) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['state', index, 'initial'],
        message: `状态初始值类型应为 ${variable.type}`,
      });
    }
  }
  const forbiddenFields = new Set(['__proto__', 'constructor', 'prototype']);
  for (const [collectionIndex, collection] of model.entities.entries()) {
    if (forbiddenFields.has(collection.idField)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entities', collectionIndex, 'idField'],
        message: `实体编号字段不安全：${collection.idField}`,
      });
    }
    const ids = new Set<string>();
    for (const [entityIndex, entity] of collection.initial.entries()) {
      for (const field of Object.keys(entity)) {
        if (forbiddenFields.has(field)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['entities', collectionIndex, 'initial', entityIndex, field],
            message: `实体字段名称不安全：${field}`,
          });
        }
      }
      const id = entity[collection.idField];
      if (id === undefined)
        continue;
      const normalizedId = String(id);
      if (ids.has(normalizedId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['entities', collectionIndex, 'initial', entityIndex],
          message: `初始实体编号重复：${normalizedId}`,
        });
      }
      ids.add(normalizedId);
    }
  }
  for (const [index, output] of model.outputs.entries()) {
    const fields = new Set(Object.keys(output.fields));
    for (const dimension of output.dimensions) {
      if (!fields.has(dimension.key)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['outputs', index, 'dimensions'],
          message: `数据维度缺少对应输出字段：${dimension.key}`,
        });
      }
    }
  }

  for (const [index, action] of actions.entries()) {
    const actionFields = action.type === 'entity.create'
      ? Object.keys(action.values)
      : action.type === 'entity.update'
        ? Object.keys(action.set)
        : action.type === 'event.schedule'
          ? Object.keys(action.payload)
          : [];
    for (const field of actionFields) {
      if (forbiddenFields.has(field)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['actions', index],
          message: `动作字段名称不安全：${field}`,
        });
      }
    }
    if (
      (action.type === 'state.set' || action.type === 'state.increment')
      && !stateIds.has(action.target)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['actions', index, 'target'],
        message: `动作引用了未知状态变量：${action.target}`,
      });
    }
    if (
      action.type === 'state.increment'
      && stateById.get(action.target)?.type !== 'number'
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['actions', index, 'target'],
        message: `增量动作只能用于数值状态变量：${action.target}`,
      });
    }
    if (
      action.type.startsWith('entity.')
      && 'collection' in action
      && !collectionIds.has(action.collection)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['actions', index, 'collection'],
        message: `动作引用了未知实体集合：${action.collection}`,
      });
    }
  }
});

export type ModelParameter = z.infer<typeof modelParameterSchema>;
export type ModelStateVariable = z.infer<typeof modelStateVariableSchema>;
export type ModelEntityCollection = z.infer<typeof modelEntityCollectionSchema>;
export type ModelAction = z.infer<typeof modelActionSchema>;
export type ModelTimer = z.infer<typeof modelTimerSchema>;
export type ModelTransition = z.infer<typeof modelTransitionSchema>;
export type ModelDerivedValue = z.infer<typeof modelDerivedValueSchema>;
export type ModelObservationOutput = z.infer<
  typeof modelObservationOutputSchema
>;
export type ModelInteractionRule = z.infer<typeof modelInteractionRuleSchema>;
export type ModelExecutionLimits = z.infer<typeof modelExecutionLimitsSchema>;
export type ModelSpec = z.infer<typeof modelSpecSchema>;
